/**
 * PublicLegalSignPage — no-auth signing page reached via the emailed link
 * `/sign-agreement/:token`. Lets the client:
 *   • preview the agreement body
 *   • download the current PDF
 *   • sign online by typing their name (Type-sign tab), OR
 *   • upload a signed PDF they printed and signed offline (Upload tab)
 *
 * On successful typed sign, the backend stamps a signature panel onto the
 * agreement PDF and stores it as the client's signed copy.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  FileSignature, Download, CheckCircle2, Loader2,
  Upload, PenLine, ShieldCheck, PenTool, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Normalise legacy content where whole pipe-tables live on one line
// (`| A | B | |---|---| | v1 | v2 |`). Detect the embedded separator,
// derive the column count and split at every (ncols+1)th pipe so each
// row lands on its own line. Prefix text stays as-is.
function normaliseMashedTables(md) {
  if (!md) return "";
  const outLines = [];
  for (const line of md.split("\n")) {
    const sep = line.match(/\|(?:\s*[-:]+\s*\|){2,}/);
    if (!sep) { outLines.push(line); continue; }
    const ncols = (sep[0].match(/\|/g) || []).length - 1;
    const firstPipe = line.indexOf("|");
    const prefix = line.slice(0, firstPipe).trimEnd();
    if (prefix) outLines.push(prefix);
    const table = line.slice(firstPipe);
    let buf = "", pipes = 0;
    for (const ch of table) {
      buf += ch;
      if (ch === "|") {
        pipes++;
        if (pipes === ncols + 1) {
          outLines.push(buf.trim());
          buf = ""; pipes = 0;
        }
      }
    }
    const tail = buf.trim();
    if (tail) outLines.push(tail);
  }
  return outLines.join("\n");
}

// Tiny markdown renderer — mirrors the light subset the backend PDF
// renderer supports (headings, bold/italic, lists, horizontal rule,
// pipe tables). Good enough for the on-screen preview.
function renderMd(md) {
  if (!md) return null;
  const lines = normaliseMashedTables(md).replace(/\r/g, "").split("\n");
  const out = [];
  let i = 0;
  const inline = (s) => s
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/\*([^*]+)\*/g, "<em>$1</em>")
    .replace(/`([^`]+)`/g, "<code>$1</code>");
  const isSep = (row) => row.every((c) => /^:?-+:?$/.test(c.trim()));
  while (i < lines.length) {
    const ln = lines[i];
    if (!ln.trim()) { i++; continue; }
    if (ln.startsWith("### ")) {
      out.push(<h4 key={i} className="font-semibold text-sm mt-4 mb-1"
        dangerouslySetInnerHTML={{ __html: inline(ln.slice(4)) }} />);
      i++; continue;
    }
    if (ln.startsWith("## ")) {
      out.push(<h3 key={i} className="font-display font-bold text-base mt-5 mb-2 text-[#002FA7]"
        dangerouslySetInnerHTML={{ __html: inline(ln.slice(3)) }} />);
      i++; continue;
    }
    if (ln.startsWith("# ")) {
      out.push(<h2 key={i} className="font-display font-bold text-lg mt-6 mb-2"
        dangerouslySetInnerHTML={{ __html: inline(ln.slice(2)) }} />);
      i++; continue;
    }
    if (ln.trim() === "---") {
      out.push(<hr key={i} className="my-3 border-zinc-200" />);
      i++; continue;
    }
    // Pipe table
    if (ln.trim().startsWith("|") && ln.trim().endsWith("|") && ln.includes("|", 1)) {
      const rows = [];
      while (i < lines.length && lines[i].trim().startsWith("|")
             && lines[i].trim().endsWith("|")) {
        const cells = lines[i].trim().slice(1, -1).split("|").map((c) => c.trim());
        rows.push(cells);
        i++;
      }
      if (rows.length) {
        const filtered = rows.filter((r) => !isSep(r));
        const header = filtered[0] || [];
        const body = filtered.slice(1);
        // If the first row is all-empty (leading `| |` header spacer), still
        // render it as th so column widths line up.
        out.push(
          <div key={`tbl${i}`} className="overflow-x-auto my-3">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-zinc-50 border-b border-zinc-200">
                  {header.map((c, k) => (
                    <th key={k} className="text-left py-2 px-2 font-semibold text-zinc-700 align-top"
                      dangerouslySetInnerHTML={{ __html: inline(c) }} />
                  ))}
                </tr>
              </thead>
              <tbody>
                {body.map((r, ri) => (
                  <tr key={ri} className="border-b border-zinc-100 align-top">
                    {r.map((c, k) => (
                      <td key={k} className="py-2 px-2"
                        dangerouslySetInnerHTML={{ __html: inline(c) }} />
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>);
      }
      continue;
    }
    if (/^\s*[-*•]\s/.test(ln)) {
      const items = [];
      while (i < lines.length && /^\s*[-*•]\s/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*•]\s/, ""));
        i++;
      }
      out.push(
        <ul key={`ul${i}`} className="list-disc pl-5 my-2 text-sm space-y-1">
          {items.map((t, k) => (
            <li key={k} dangerouslySetInnerHTML={{ __html: inline(t) }} />
          ))}
        </ul>);
      continue;
    }
    if (/^\s*\d+\.\s/.test(ln)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s/, ""));
        i++;
      }
      out.push(
        <ol key={`ol${i}`} className="list-decimal pl-5 my-2 text-sm space-y-1">
          {items.map((t, k) => (
            <li key={k} dangerouslySetInnerHTML={{ __html: inline(t) }} />
          ))}
        </ol>);
      continue;
    }
    // Paragraph
    const para = [ln];
    i++;
    while (i < lines.length && lines[i].trim()
           && !/^(#{1,3}\s|---$|\s*[-*•]\s|\s*\d+\.\s|\|)/.test(lines[i])) {
      para.push(lines[i]); i++;
    }
    out.push(<p key={`p${i}`} className="text-sm my-2 leading-relaxed"
      dangerouslySetInnerHTML={{ __html: inline(para.join(" ")) }} />);
  }
  return out;
}

// ── Minimal canvas signature pad — supports mouse + touch input,
// exports a trimmed white-background PNG data URL. Kept in-file to avoid
// pulling in a heavyweight npm dep.
function SignaturePad({ onChange, height = 180, testId = "signature-pad" }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });
  const dirtyRef = useRef(false);

  const setup = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = height * dpr;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, height);
    ctx.strokeStyle = "#0B0B0F";
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, [height]);

  useEffect(() => {
    setup();
    const onResize = () => setup();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [setup]);

  const pos = (evt) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const t = evt.touches ? evt.touches[0] : evt;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const start = (evt) => {
    evt.preventDefault();
    drawingRef.current = true;
    lastRef.current = pos(evt);
  };
  const move = (evt) => {
    if (!drawingRef.current) return;
    evt.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(evt);
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
    dirtyRef.current = true;
  };
  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    if (dirtyRef.current && onChange) {
      onChange(canvasRef.current.toDataURL("image/png"));
    }
  };

  const clear = () => {
    setup();
    dirtyRef.current = false;
    if (onChange) onChange("");
  };

  return (
    <div className="space-y-2">
      <div className="relative rounded-sm border-2 border-dashed border-zinc-300 bg-white">
        <canvas ref={canvasRef}
          data-testid={testId}
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          style={{ width: "100%", height, touchAction: "none", cursor: "crosshair" }} />
        <div className="absolute bottom-1 left-3 text-[10px] text-zinc-400 pointer-events-none">
          ✒ Sign above with your mouse or finger
        </div>
      </div>
      <button type="button" onClick={clear}
        className="text-[11px] text-zinc-600 hover:text-zinc-900 flex items-center gap-1"
        data-testid={`${testId}-clear`}>
        <RotateCcw className="h-3 w-3" /> Clear signature
      </button>
    </div>
  );
}

export default function PublicLegalSignPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("type"); // 'type' | 'draw' | 'upload'
  const [signerName, setSignerName] = useState("");
  const [signerEmail, setSignerEmail] = useState("");
  const [typedSig, setTypedSig] = useState("");
  const [drawnSig, setDrawnSig] = useState(""); // data URL PNG
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [done, setDone] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/legal-documents/public/sign/${token}`);
      setInfo(data);
      setSignerName(data.requested_name || data.client_name || "");
      setSignerEmail(data.requested_to || "");
      setTypedSig(data.requested_name || data.client_name || "");
      if (data.already_signed) setDone(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "This signing link is invalid or has expired.");
    } finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const pdfHref = useMemo(
    () => `${API}/legal-documents/public/sign/${token}/pdf`, [token]);

  const submitTypeSign = async () => {
    if (!signerName.trim() || signerName.trim().length < 2) {
      toast.error("Please enter your full name"); return;
    }
    if (!typedSig.trim()) { toast.error("Type your signature"); return; }
    if (!ack) { toast.error("Please acknowledge the electronic-signature terms"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/legal-documents/public/sign/${token}/type-sign`, {
        signer_name: signerName.trim(),
        signer_email: signerEmail.trim() || null,
        typed_signature: typedSig.trim(),
        signed_via: "typed",
        acknowledged: true,
      });
      setDone(true);
      toast.success("Agreement signed — thank you");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Signing failed");
    } finally { setBusy(false); }
  };

  const submitDrawnSign = async () => {
    if (!signerName.trim() || signerName.trim().length < 2) {
      toast.error("Please enter your full name"); return;
    }
    if (!drawnSig) { toast.error("Please draw your signature above"); return; }
    if (!ack) { toast.error("Please acknowledge the electronic-signature terms"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/legal-documents/public/sign/${token}/type-sign`, {
        signer_name: signerName.trim(),
        signer_email: signerEmail.trim() || null,
        typed_signature: signerName.trim(),
        signature_image_b64: drawnSig,
        signed_via: "drawn",
        acknowledged: true,
      });
      setDone(true);
      toast.success("Agreement signed — thank you");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Signing failed");
    } finally { setBusy(false); }
  };

  const submitUploadSign = async () => {
    if (!uploadFile) { toast.error("Choose the signed PDF first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      fd.append("signer_name", signerName.trim());
      fd.append("signer_email", signerEmail.trim());
      await axios.post(
        `${API}/legal-documents/public/sign/${token}/upload-signed`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
      toast.success("Signed copy uploaded — thank you");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <Loader2 className="h-6 w-6 animate-spin text-[#002FA7]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4">
        <Card className="max-w-md rounded-sm" data-testid="legal-sign-error">
          <CardContent className="p-6 space-y-2">
            <div className="text-lg font-display font-bold">Link unavailable</div>
            <p className="text-sm text-zinc-600">{error}</p>
            <p className="text-xs text-zinc-500">
              Please reach out to {info?.document_company || "Iceberg Digital"} for a fresh signing link.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4">
        <Card className="max-w-lg w-full rounded-sm" data-testid="legal-sign-success">
          <CardContent className="p-6 space-y-3 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8 text-emerald-600" />
            </div>
            <div className="text-xl font-display font-bold">
              {info?.status === "signed" && info?.already_signed
                ? "Already signed" : "Agreement signed"}
            </div>
            <p className="text-sm text-zinc-600">
              Thanks{signerName ? `, ${signerName}` : ""}. A signed copy has been
              stored on the client record and is available to{" "}
              {info?.document_company || "the team"} for reference.
            </p>
            <div className="pt-2 text-[12px] text-zinc-500">
              <div><strong>{info?.document_title}</strong> · {info?.version_string}</div>
              {info?.signed_date && <div>Signed: {info.signed_date}</div>}
            </div>
            <Button asChild variant="outline" className="mt-3">
              <a href={pdfHref} target="_blank" rel="noreferrer" data-testid="legal-sign-download-pdf">
                <Download className="h-4 w-4 mr-1.5" /> Download agreement PDF
              </a>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-4">
        {/* Header */}
        <Card className="rounded-sm">
          <CardContent className="p-5 flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-[240px]">
              <div className="text-[11px] uppercase tracking-widest text-zinc-500">
                {info?.document_company}
              </div>
              <h1 className="font-display font-bold text-2xl mt-0.5">
                {info?.document_title}
              </h1>
              <div className="flex items-center gap-2 mt-1 text-xs text-zinc-600">
                <span className="font-mono">{info?.version_string}</span>
                {info?.effective_date && <span>· Effective {info.effective_date}</span>}
                <Badge className="bg-amber-50 text-amber-800 border-amber-200 rounded-sm">
                  Awaiting signature
                </Badge>
              </div>
              <p className="text-sm text-zinc-600 mt-2">
                Please review the agreement below, then choose how you&apos;d
                like to sign — type your signature online, or download &amp;
                upload a signed PDF.
              </p>
            </div>
            <Button asChild variant="outline" className="rounded-sm">
              <a href={pdfHref} target="_blank" rel="noreferrer" data-testid="legal-sign-pdf-link">
                <Download className="h-4 w-4 mr-1.5" /> Download PDF
              </a>
            </Button>
          </CardContent>
        </Card>

        {/* Document preview */}
        <Card className="rounded-sm">
          <CardContent className="p-6 max-h-[52vh] overflow-y-auto" data-testid="legal-sign-doc-body">
            {renderMd(info?.body_md)}
            {info?.footer_note && (
              <p className="text-xs italic text-zinc-500 mt-4">{info.footer_note}</p>
            )}
          </CardContent>
        </Card>

        {/* Signature tabs */}
        <Card className="rounded-sm">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center gap-2 border-b border-zinc-200 pb-3">
              <FileSignature className="h-4 w-4 text-[#002FA7]" />
              <span className="font-display font-bold">Sign this agreement</span>
            </div>
            <div className="flex gap-2 -mx-1">
              <button type="button"
                onClick={() => setTab("type")}
                className={`flex-1 rounded-sm border px-3 py-2 text-sm flex items-center justify-center gap-1.5
                  ${tab === "type" ? "border-[#002FA7] bg-[#002FA7]/5 text-[#002FA7] font-semibold"
                    : "border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}
                data-testid="legal-sign-tab-type">
                <PenLine className="h-4 w-4" /> Type
              </button>
              <button type="button"
                onClick={() => setTab("draw")}
                className={`flex-1 rounded-sm border px-3 py-2 text-sm flex items-center justify-center gap-1.5
                  ${tab === "draw" ? "border-[#002FA7] bg-[#002FA7]/5 text-[#002FA7] font-semibold"
                    : "border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}
                data-testid="legal-sign-tab-draw">
                <PenTool className="h-4 w-4" /> Draw
              </button>
              <button type="button"
                onClick={() => setTab("upload")}
                className={`flex-1 rounded-sm border px-3 py-2 text-sm flex items-center justify-center gap-1.5
                  ${tab === "upload" ? "border-[#002FA7] bg-[#002FA7]/5 text-[#002FA7] font-semibold"
                    : "border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}
                data-testid="legal-sign-tab-upload">
                <Upload className="h-4 w-4" /> Upload signed PDF
              </button>
            </div>

            {tab === "type" && (
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px]">Full name</Label>
                    <Input value={signerName}
                      onChange={(e) => setSignerName(e.target.value)}
                      data-testid="legal-sign-name" />
                  </div>
                  <div>
                    <Label className="text-[11px]">Email (optional)</Label>
                    <Input type="email" value={signerEmail}
                      onChange={(e) => setSignerEmail(e.target.value)}
                      data-testid="legal-sign-email" />
                  </div>
                </div>
                <div>
                  <Label className="text-[11px]">Type your signature</Label>
                  <input value={typedSig}
                    onChange={(e) => setTypedSig(e.target.value)}
                    className="w-full mt-1 rounded-sm border border-zinc-300 px-3 py-3
                               text-2xl italic font-serif focus:border-[#002FA7] outline-none"
                    style={{ fontFamily: '"Segoe Script", "Brush Script MT", cursive' }}
                    data-testid="legal-sign-typed-signature" />
                  <div className="text-[11px] text-zinc-500 mt-1">
                    This typed signature will be inserted onto the executed PDF.
                  </div>
                </div>
                <label className="flex items-start gap-2 text-xs text-zinc-700 cursor-pointer">
                  <Checkbox checked={ack} onCheckedChange={(v) => setAck(!!v)}
                    data-testid="legal-sign-ack" />
                  <span>
                    I acknowledge that by typing my name above and pressing
                    <b> Sign agreement</b>, I am legally executing this document
                    under Australia&apos;s <em>Electronic Transactions Act 1999</em>.
                    My IP address, browser and timestamp will be recorded as
                    evidence of signing.
                  </span>
                </label>
                <div className="flex items-center gap-2 text-[11px] text-zinc-500">
                  <ShieldCheck className="h-3.5 w-3.5" /> Your signature is
                  recorded with an audit trail (IP, user-agent, timestamp).
                </div>
                <Button className="w-full rounded-sm bg-[#002FA7] hover:bg-[#001F7A]"
                  onClick={submitTypeSign} disabled={busy || !ack}
                  data-testid="legal-sign-submit-type">
                  {busy ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Signing…</>
                    : <><FileSignature className="h-4 w-4 mr-1.5" /> Sign agreement</>}
                </Button>
              </div>
            )}

            {tab === "draw" && (
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px]">Full name</Label>
                    <Input value={signerName}
                      onChange={(e) => setSignerName(e.target.value)}
                      data-testid="legal-sign-draw-name" />
                  </div>
                  <div>
                    <Label className="text-[11px]">Email (optional)</Label>
                    <Input type="email" value={signerEmail}
                      onChange={(e) => setSignerEmail(e.target.value)}
                      data-testid="legal-sign-draw-email" />
                  </div>
                </div>
                <div>
                  <Label className="text-[11px]">Sign in the box below</Label>
                  <SignaturePad onChange={setDrawnSig}
                    testId="legal-sign-canvas" />
                </div>
                <label className="flex items-start gap-2 text-xs text-zinc-700 cursor-pointer">
                  <Checkbox checked={ack} onCheckedChange={(v) => setAck(!!v)}
                    data-testid="legal-sign-draw-ack" />
                  <span>
                    I acknowledge that by drawing my signature above and
                    pressing <b>Sign agreement</b>, I am legally executing
                    this document under Australia&apos;s
                    <em> Electronic Transactions Act 1999</em>. My IP address,
                    browser and timestamp will be recorded as evidence of
                    signing.
                  </span>
                </label>
                <div className="flex items-center gap-2 text-[11px] text-zinc-500">
                  <ShieldCheck className="h-3.5 w-3.5" /> Your signature is
                  recorded with an audit trail (IP, user-agent, timestamp).
                </div>
                <Button className="w-full rounded-sm bg-[#002FA7] hover:bg-[#001F7A]"
                  onClick={submitDrawnSign} disabled={busy || !ack || !drawnSig}
                  data-testid="legal-sign-submit-draw">
                  {busy ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Signing…</>
                    : <><FileSignature className="h-4 w-4 mr-1.5" /> Sign agreement</>}
                </Button>
              </div>
            )}

            {tab === "upload" && (
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px]">Your name</Label>
                    <Input value={signerName}
                      onChange={(e) => setSignerName(e.target.value)}
                      data-testid="legal-sign-upload-name" />
                  </div>
                  <div>
                    <Label className="text-[11px]">Email (optional)</Label>
                    <Input type="email" value={signerEmail}
                      onChange={(e) => setSignerEmail(e.target.value)}
                      data-testid="legal-sign-upload-email" />
                  </div>
                </div>
                <div>
                  <Label className="text-[11px]">Signed PDF or scan</Label>
                  <Input type="file" accept=".pdf,image/*"
                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                    data-testid="legal-sign-upload-file" />
                  <div className="text-[11px] text-zinc-500 mt-1">
                    Download the PDF above, sign it (print &amp; scan, or
                    annotate with a PDF tool), then upload it here. Max 30 MB.
                  </div>
                </div>
                <Button className="w-full rounded-sm bg-[#002FA7] hover:bg-[#001F7A]"
                  onClick={submitUploadSign} disabled={busy || !uploadFile}
                  data-testid="legal-sign-submit-upload">
                  {busy ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Uploading…</>
                    : <><Upload className="h-4 w-4 mr-1.5" /> Submit signed copy</>}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="text-center text-[11px] text-zinc-500 pb-4">
          Powered by {info?.document_company || "Iceberg Digital"} · Secure electronic signing
        </div>
      </div>
    </div>
  );
}
